import { DateTime } from "luxon";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import {
  alpha_num,
  confirmed,
  email,
  ext,
  max,
  max_value,
  min,
  min_value,
  numeric,
  regex,
  required,
} from "vee-validate/dist/rules";
import Vue from "vue";

extend("required", {
  ...required,
  message: "This field is required",
});

extend("currency", {
  validate: (value) => {
    return /^\d{1,15}(\.\d{1,3})?$/.test(value);
  },
  message:
    "This field must be a valid currency amount with up to 15 digits and 3 decimal places",
});
extend("exchange_rate", {
  validate: (value) => {
    return /^\d{1,9}(\.\d{1,2})?$/.test(value);
  },
  message:
    "This field must be a valid exchange rate amount with up to 9 digits and 2 decimal places",
});

extend("min", {
  ...min,
  message: "This field must be {length} characters or more",
});
extend("min_value", {
  ...min_value,
  message: "This field must be min {min}",
});
extend("max_value", {
  ...max_value,
  message: "This field must be max {max}",
});

extend("max", {
  ...max,
  message: "This field must be {length} characters or less",
});

extend("email", {
  ...email,
  message: "This field must be a valid email",
});
extend("numeric", {
  ...numeric,
  message: "The field may only contain numeric characters",
});
extend("alpha_num", {
  ...alpha_num,
  message: "The field may only contain numbers and letters",
});
extend("confirmed", {
  ...confirmed,
  message: "Values should be same",
});

extend("tc_id", {
  ...required,
  ...numeric,
  message: "Enter a TC Id No",
});
extend("emaill", {
  ...required,
  ...email,
  message: "This field must be a valid email",
});
extend("ext", {
  ...ext,
  message: "This field must be a valid file",
});

extend("regex", regex);

extend("sameDomain", {
  params: ["target"],
  validate(value, { target }) {
    let domain = value.split("@")[1];
    if (Array.isArray(target)) {
      return !!target.find((v) => v === domain);
    } else return domain === target;
  },
  message: "Users email addresses must be from contractor domain",
});
extend("dateRange", {
  ...required,
  validate(value) {
    return value && value.length >= 2;
  },
  message: "This field is required",
});

extend("url", {
  validate(value) {
    const pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$",
      "i",
    ); // fragment locator
    return pattern.test(value);
  },
  message: "Please enter a valid link.",
});
extend("is_time", {
  validate: (value) => {
    let regex = new RegExp("([0-1][0-9]|2[0-3]):([0-5][0-9])");
    return value && regex.test(value);
  },
  message: "This field must be a valid time",
});
extend("is_date", {
  validate: (value) => {
    let regex = new RegExp(
      "([0-9][0-9][0-9][0-9])-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1])",
    );
    return value && regex.test(value);
  },
  message: "This field must be a valid date",
});

extend("is_date_project", {
  validate: (value) => {
    if (!value) return false;
    const regex = /^([0-2][0-9]|3[0-1])\.(0[1-9]|1[0-2])\.(\d{4})$/;
    return regex.test(value);
  },
  message: "This field must be a valid date",
});

extend("is_range_date", {
  validate: (value) => {
    let regex = new RegExp(
      "([0-9][0-9][0-9][0-9])-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1]),([0-9][0-9][0-9][0-9])-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1])",
    );
    return value && regex.test(value);
  },
  message: "This field must be a valid range date",
});

extend("is_range_date_project", {
  validate: (value) => {
    let regex =
      /^([0-2][0-9]|3[0-1])\.(0[1-9]|1[0-2])\.(\d{4}),([0-2][0-9]|3[0-1])\.(0[1-9]|1[0-2])\.(\d{4})$/;
    return value && regex.test(value);
  },
  message: "This field must be a valid range date.",
});

extend("is_phone", {
  validate: (value) => {
    let regex = new RegExp(
      "[+]([0-9][0-9]) [(]([0-9][0-9][0-9])[)] ([0-9][0-9][0-9]) ([0-9][0-9]) ([0-9][0-9])",
    );
    return value && regex.test(value);
  },
  message: "This field must be a phone number.",
});

extend("max_date", {
  validate(value, args) {
    if (value) {
      if (args[0] && args[0] !== undefined) {
        return DateTime.fromISO(value) <= DateTime.fromISO(args[0]);
      }
    }
    return true;
  },
  message: `Please select a date before max date`,
});

extend("min_date", {
  validate(value, args) {
    if (value) {
      if (args[0] && args[0] !== undefined) {
        return DateTime.fromISO(value) >= DateTime.fromISO(args[0]);
      }
    }
    return true;
  },
  message: "Please select a date after min date",
});

extend("matchPassword", {
  params: ["password"],
  validate: (value, { password }) => password === value,
  message: "Passwords do not match",
});

extend("imageFileType", {
  params: ["extensions"],
  message: "This field must be a valid file",
  validate(value, { extensions }) {
    if (!value) {
      return false;
    }
    if (typeof value === "string") {
      return true;
    }

    if (!Array.isArray(value)) {
      const fileExtension = value.name.split(".").pop().toLowerCase();

      return extensions.includes(`.${fileExtension}`);
    } else {
      return !value
        .map((file) => {
          const fileExtension = file.name.split(".").pop().toLowerCase();
          return extensions.includes(`.${fileExtension}`);
        })
        .includes(false);
    }
  },
});

extend("decimal", {
  validate: (value, { decimals = "*", separator = "." } = {}) => {
    if (value === null || value === undefined || value === "") {
      return {
        valid: false,
      };
    }
    if (Number(decimals) === 0) {
      return {
        valid: /^-?\d*$/.test(value),
      };
    }
    const regexPart = decimals === "*" ? "+" : `{1,${decimals}}`;
    const regex = new RegExp(
      `^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE]{1}[-]?\\d+)?$`,
    );

    return {
      valid: regex.test(value),
    };
  },
  message: "The field must contain only decimal values",
});
extend("is_code", {
  validate: (value) => value && /^[a-zA-Z0-9.]+$/.test(value),
  message: "Field can only contain letters, numbers, and dots.",
});
extend("tc_no", {
  validate: (value) => value && /^[0-9]{11}$/.test(value),
  message: "Field can only contain 11 numbers.",
});

extend("min_value_or_star", {
  params: ["min"],
  validate(value, { min }) {
    // * her zaman geçerli
    if (value === "*") {
      return true;
    }

    // boşsa geçersiz (required ile birlikte kullanılmalı)
    if (value === null || value === undefined || value === "") {
      return false;
    }

    const num = Number(value);

    // sayı değilse
    if (isNaN(num)) {
      return false;
    }

    return num >= Number(min);
  },
  message: "This field must be min {min} or *",
});

extend("max_value_or_star", {
  params: ["max"],
  validate(value, { max }) {
    // * her zaman geçerli
    if (value === "*") {
      return true;
    }

    // boşsa geçersiz (required ile birlikte kullanılmalı)
    if (value === null || value === undefined || value === "") {
      return false;
    }

    const num = Number(value);

    // sayı değilse
    if (isNaN(num)) {
      return false;
    }

    return num <= Number(max);
  },
  message: "This field must be max {max} or *",
});

Vue.component("ValidationObserver", ValidationObserver);
Vue.component("ValidationProvider", ValidationProvider);
